import { format } from "date-fns";
import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { ErrorMessage } from "@/app/components/error-message";
import { Card } from "@/components/card";
import { LoadingScreen } from "@/components/loading-screen";
import { Badge } from "@/components/ui/badge";
import { ChartContainer } from "@/components/ui/chart";

import { useGetScheduledSessions } from "../api/get-scheduled-sessions";
import { Timeframe } from "../types/timeframe";
import { calculateMovingAverage } from "../utils/calculate-mooving-avg";
import { calculateTrendLine } from "../utils/calculate-trendline";
import { generateTimeRange } from "../utils/generate-time-range";
import { getXAxisArgsForTimeBasedGraph } from "../utils/get-a-axis-args-for-time-based-graph";
import { groupChartData } from "../utils/group-chart-data";

import { TimeframeSelector } from "./timeframe-selector";

type ScheduledSessionsProps = {
  organisationId?: string;
};

export function ScheduledSessions({ organisationId }: ScheduledSessionsProps) {
  const [timeframe, setTimeframe] = useState<Timeframe>("w");
  const { from, to } = generateTimeRange(timeframe);

  const {
    data: scheduledSessions,
    isLoading,
    isError,
    error,
    isSuccess,
  } = useGetScheduledSessions({
    from: from.toISOString(),
    to: to.toISOString(),
    organisationId,
  });

  const chartData = isSuccess
    ? scheduledSessions.map((session) => ({
        day: new Date(session.day).getTime(),
        count: session.count,
      }))
    : [];

  const groupedData = groupChartData(chartData, timeframe);

  const movingAvgData = calculateMovingAverage(groupedData, 7);

  const trendLineData = calculateTrendLine(movingAvgData);

  if (isError) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <Card.Header
            title={
              <span>
                Scheduled Sessions{" "}
                <Badge variant="outline" className="ml-1 font-normal">
                  {format(from, "MMM dd")} - {format(to, "MMM dd")}
                </Badge>
              </span>
            }
          />
          <TimeframeSelector
            value={timeframe}
            onChange={(value) => setTimeframe(value as Timeframe)}
          />
        </div>
        <Card.Content>
          <ErrorMessage error={error} />
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Card.Header
          title={
            <span>
              Scheduled Sessions{" "}
              <Badge variant="outline" className="ml-1 font-normal">
                {format(from, "MMM dd")} - {format(to, "MMM dd")}
              </Badge>
            </span>
          }
        />
        <TimeframeSelector
          value={timeframe}
          onChange={(value) => setTimeframe(value as Timeframe)}
        />
      </div>

      <Card.Content className="relative">
        {isLoading && (
          <LoadingScreen className="absolute inset-0 z-10 h-full w-full" />
        )}
        <ChartContainer config={{}} className="h-[280px] w-full">
          <LineChart data={trendLineData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              {...getXAxisArgsForTimeBasedGraph(trendLineData, timeframe)}
            />
            <YAxis />
            <Tooltip
              contentStyle={{
                backgroundColor: "#161B26",
                color: "hsl(var(--card-foreground))",
                borderRadius: "var(--radius)",
                padding: "12px",
                border: "none",
              }}
              labelFormatter={(value) => {
                const date = new Date(value);
                return format(date, "MMM dd, yyyy");
              }}
              labelClassName="text-white"
            />

            <Line
              type="monotone"
              dataKey="count"
              name="Scheduled sessions"
              scale="linear"
              dot={false}
              stroke="hsl(var(--card-foreground))"
              strokeWidth={2}
              strokeOpacity={timeframe === "w" ? 0.7 : 0.3}
            />
            {timeframe !== "w" && (
              <Line
                type="monotone"
                dataKey="movingAvg"
                name="7 days moving average"
                stroke="hsl(var(--warning))"
                strokeWidth={3}
                dot={false}
              />
            )}

            {/* Trend line */}
            <Line
              type="monotone"
              dataKey="trend"
              name="Trend"
              stroke="hsl(var(--card-foreground))"
              strokeWidth={2}
              dot={false}
              strokeDasharray="5 5"
            />
          </LineChart>
        </ChartContainer>
      </Card.Content>
    </Card>
  );
}
